import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withDirectives as _withDirectives, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "w-full px-2 sm:px-6 py-4" }
const _hoisted_2 = {
  key: 0,
  class: "mb-4 -mt-6"
}
const _hoisted_3 = { class: "bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" }
const _hoisted_4 = {
  key: 0,
  class: "mb-4 text-sm text-black"
}
const _hoisted_5 = {
  key: 1,
  class: "mb-4 text-sm text-black"
}
const _hoisted_6 = { key: 2 }
const _hoisted_7 = ["value"]
const _hoisted_8 = {
  key: 3,
  class: "text-sm text-red-600"
}
const _hoisted_9 = { class: "flex justify-end gap-3 pt-5" }
const _hoisted_10 = ["disabled"]

import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { PageHeader, PackageForm, PackageTable, PackageEditModal, ModalComp } from '@/components'
import { getPacksByVendeur, getAllPacks, createPack, updatePack, packHasProducts, deletePack } from '@/api'
import { useAuthStore } from '@/stores/auth'
import { useNotification } from '@/composables/useNotification'
import type { Pack, PackageItem, PackageFormData, CreatePackInput } from '@/types/pack'
import {useRouter} from "vue-router";


export default /*@__PURE__*/_defineComponent({
  __name: 'PackageManagementView',
  setup(__props) {

const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()

// Déterminer la page de retour selon le paramètre 'from'
const fromPage = computed(() => route.query.from as string | undefined)
const returnRoute = computed(() => {
  if (fromPage.value === 'mel') return '/catalog/mel'
  return null
})
// Notifications
const { success: showSuccess, error: showError } = useNotification()

const packs = ref<Pack[]>([])
const allPacks = ref<Pack[]>([]) // Tous les paquets pour les templates
const isLoading = ref(false)

// État de la modal de modification
const showEditModal = ref(false)
const editingPack = ref<Pack | null>(null)
const showDeleteModal = ref(false)
const deletingPackage = ref<PackageItem | null>(null)
const replacementPackageId = ref<number | ''>('')
const isDeleting = ref(false)
const hasBoundProducts = ref(false)

const getPackLength = (pack: Pack) => {
  const quantites = [
    pack.p1, pack.p2, pack.p3, pack.p4, pack.p5, pack.p6,
    pack.p7, pack.p8, pack.p9, pack.p10, pack.p11, pack.p12
  ]
  return quantites.reduce(
    (lastIndex, quantite, index) => Number(quantite) > 0 ? index + 1 : lastIndex,
    0
  )
}

// Transformer tous les packs pour les templates
const allPackagesForTemplates = computed(() => {
  return allPacks.value.map(pack => {
    const toutesQuantites = [
      pack.p1, pack.p2, pack.p3, pack.p4, pack.p5, pack.p6,
      pack.p7, pack.p8, pack.p9, pack.p10, pack.p11, pack.p12
    ]
    
    const nombreTailles = getPackLength(pack)
    const quantites = toutesQuantites.slice(0, nombreTailles)
    
    return {
      id: pack.id_pack,
      titre: pack.titre || 'Sans titre',
      quantites,
      nombreTailles,
      dateCreation: new Date().toISOString().split('T')[0]
    }
  }).filter(pack =>
    pack.nombreTailles > 0 && pack.quantites.every(quantite => quantite > 0)
  )
})

// Transformer les packs pour le composant PackageTable avec groupement
const packages = computed(() => {
  const transformedPacks = packs.value
    .map(pack => {
      const toutesQuantites = [
        pack.p1, pack.p2, pack.p3, pack.p4, pack.p5, pack.p6,
        pack.p7, pack.p8, pack.p9, pack.p10, pack.p11, pack.p12
      ]
      
      const nombreTailles = getPackLength(pack)
      const quantites = toutesQuantites.slice(0, nombreTailles)
      
      return {
        id: pack.id_pack,
        titre: pack.titre || 'Sans titre',
        quantites,
        nombreTailles,
        dateCreation: new Date().toISOString().split('T')[0]
      }
    })
    .sort((a, b) => a.nombreTailles - b.nombreTailles) // Tri par ordre croissant du nombre de tailles
  
  // Grouper par nombre de tailles
  const grouped = transformedPacks.reduce((groups, pack) => {
    const key = pack.nombreTailles
    if (!groups[key]) {
      groups[key] = []
    }
    groups[key].push(pack)
    return groups
  }, {} as Record<number, typeof transformedPacks>)
  
  // Retourner les groupes triés par nombre de tailles
  return Object.keys(grouped)
    .map(Number)
    .sort((a, b) => a - b)
    .flatMap(nombreTailles => grouped[nombreTailles])
})

const replacementPackages = computed(() => {
  if (!deletingPackage.value) return []
  return packages.value.filter(
    pack =>
      pack.id !== deletingPackage.value?.id &&
      pack.nombreTailles === deletingPackage.value?.nombreTailles
  )
})

// Méthodes de gestion des données
const loadPackages = async () => {
  if (!authStore.user?.id_vendeur) {
    return
  }

  isLoading.value = true
  
  try {
    // Charger les paquets du vendeur connecté
    packs.value = await getPacksByVendeur(authStore.user.id_vendeur)
    
    // Charger TOUS les paquets pour les templates
    allPacks.value = await getAllPacks()
  } catch (err) {
    showError('Erreur lors du chargement des paquets')
  } finally {
    isLoading.value = false
  }
}

const handleAddPackage = async (formData: PackageFormData) => {
  if (!authStore.user?.id_vendeur) {
    return
  }

  try {
    // Créer l'objet avec les quantités directement depuis formData
    const input: CreatePackInput = {
      id_vendeur: authStore.user.id_vendeur,
      titre: formData.titre || null,
      p1: formData.p1 || 0,
      p2: formData.p2 || 0,
      p3: formData.p3 || 0,
      p4: formData.p4 || 0,
      p5: formData.p5 || 0,
      p6: formData.p6 || 0,
      p7: formData.p7 || 0,
      p8: formData.p8 || 0,
      p9: formData.p9 || 0,
      p10: formData.p10 || 0,
      p11: formData.p11 || 0,
      p12: formData.p12 || 0,
    }
    
    const newPack = await createPack(input)
    packs.value = [newPack, ...packs.value]
    showSuccess(`Paquet "${newPack.titre}" créé avec succès !`)
  } catch (err) {
    showError(err instanceof Error ? err.message : 'Erreur lors de la création du paquet')
  }
}

const handleEditPackage = (packageId: number) => {
  const pack = packs.value.find(p => p.id_pack === packageId)
  if (pack) {
    editingPack.value = { ...pack }
    showEditModal.value = true
  }
}

const handleUpdatePackage = async (updatedPack: Pack) => {
  try {
    const result = await updatePack({
      id_pack: updatedPack.id_pack,
      titre: updatedPack.titre || undefined,
      p1: updatedPack.p1,
      p2: updatedPack.p2,
      p3: updatedPack.p3,
      p4: updatedPack.p4,
      p5: updatedPack.p5,
      p6: updatedPack.p6,
      p7: updatedPack.p7,
      p8: updatedPack.p8,
      p9: updatedPack.p9,
      p10: updatedPack.p10,
      p11: updatedPack.p11,
      p12: updatedPack.p12,
    })
    
    const index = packs.value.findIndex(p => p.id_pack === result.id_pack)
    if (index !== -1) {
      packs.value[index] = result
    }
    
    showEditModal.value = false
    showSuccess(`Paquet "${result.titre || 'Sans titre'}" modifié avec succès !`)
  } catch (err) {
    showError('Erreur lors de la modification du paquet')
  }
}

const handleDeletePackage = async (packageId: number) => {
  deletingPackage.value = packages.value.find(pack => pack.id === packageId) || null
  if (!deletingPackage.value) return

  try {
    hasBoundProducts.value = await packHasProducts(deletingPackage.value.id)
    replacementPackageId.value = ''
    showDeleteModal.value = true
  } catch (err) {
    deletingPackage.value = null
    showError(err instanceof Error ? err.message : 'Erreur lors de la vérification du paquet')
  }
}

const closeDeleteModal = (visible: boolean) => {
  if (isDeleting.value) return
  showDeleteModal.value = visible
  if (!visible) {
    deletingPackage.value = null
    replacementPackageId.value = ''
  }
}

const confirmDeletePackage = async () => {
  if (
    !deletingPackage.value ||
    (hasBoundProducts.value && !replacementPackageId.value) ||
    isDeleting.value
  ) return

  isDeleting.value = true
  try {
    await deletePack(
      deletingPackage.value.id,
      hasBoundProducts.value ? Number(replacementPackageId.value) : null,
    )
    const deletedTitle = deletingPackage.value.titre
    showDeleteModal.value = false
    deletingPackage.value = null
    replacementPackageId.value = ''
    await loadPackages()
    showSuccess(`Paquet "${deletedTitle}" supprimé avec succès !`)
  } catch (err) {
    showError(err instanceof Error ? err.message : 'Erreur lors de la suppression du paquet')
  } finally {
    isDeleting.value = false
  }
}

onMounted(async () => {


  if (!authStore.isInitialized) {
    await authStore.checkAuth()
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: 'auth' })
    return
  }
  await loadPackages()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(PageHeader), {
      title: "Gestion des paquets",
      subtitle: "Créez et gérez vos paquets de produits",
      class: "mb-0 pr-10"
    }),
    _createElementVNode("div", _hoisted_1, [
      (returnRoute.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createElementVNode("button", {
              onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).push(returnRoute.value))),
              class: "inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-md hover:bg-blue-700 shadow-sm transition-colors"
            }, _cache[4] || (_cache[4] = [
              _createElementVNode("i", { class: "fas fa-arrow-left mr-2" }, null, -1),
              _createTextVNode(" Retour à la création de produit ")
            ]))
          ]))
        : _createCommentVNode("", true),
      _createVNode(_unref(PackageForm), {
        "packages-existants": allPackagesForTemplates.value,
        onSubmit: handleAddPackage
      }, null, 8, ["packages-existants"]),
      _createElementVNode("div", _hoisted_3, [
        _cache[5] || (_cache[5] = _createElementVNode("div", { class: "px-6 py-4 border-b border-gray-200" }, [
          _createElementVNode("h3", { class: "text-lg font-medium text-black" }, "Mes paquets")
        ], -1)),
        _createVNode(_unref(PackageTable), {
          packages: packages.value,
          onEdit: handleEditPackage,
          onDelete: handleDeletePackage
        }, null, 8, ["packages"])
      ])
    ]),
    _createVNode(_unref(PackageEditModal), {
      "is-visible": showEditModal.value,
      pack: editingPack.value,
      "onUpdate:isVisible": _cache[1] || (_cache[1] = ($event: any) => (showEditModal.value = $event)),
      onSubmit: handleUpdatePackage
    }, null, 8, ["is-visible", "pack"]),
    _createVNode(_unref(ModalComp), {
      "is-visible": showDeleteModal.value,
      title: "Supprimer le paquet",
      size: "sm",
      "hide-footer": true,
      "onUpdate:isVisible": closeDeleteModal
    }, {
      body: _withCtx(() => [
        (hasBoundProducts.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_4, [
              _cache[6] || (_cache[6] = _createTextVNode(" Tous les produits utilisant ")),
              _createElementVNode("strong", null, "« " + _toDisplayString(deletingPackage.value?.titre) + " »", 1),
              _cache[7] || (_cache[7] = _createTextVNode(" seront associés au paquet sélectionné. "))
            ]))
          : (_openBlock(), _createElementBlock("p", _hoisted_5, " Ce paquet n'est associé à aucun produit. Il peut être supprimé sans remplacement. ")),
        (hasBoundProducts.value && replacementPackages.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _cache[9] || (_cache[9] = _createElementVNode("label", { class: "block mb-2 text-sm font-medium text-black" }, " Paquet de remplacement ", -1)),
              _withDirectives(_createElementVNode("select", {
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((replacementPackageId).value = $event)),
                class: "w-full px-3 py-2 border border-gray-300 rounded-md"
              }, [
                _cache[8] || (_cache[8] = _createElementVNode("option", { value: "" }, "Sélectionner un paquet", -1)),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(replacementPackages.value, (pkg) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: pkg.id,
                    value: pkg.id
                  }, _toDisplayString(pkg.titre) + " (" + _toDisplayString(pkg.quantites.join('-')) + ") ", 9, _hoisted_7))
                }), 128))
              ], 512), [
                [
                  _vModelSelect,
                  replacementPackageId.value,
                  void 0,
                  { number: true }
                ]
              ])
            ]))
          : (hasBoundProducts.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_8, " Créez d'abord un autre paquet avec le même nombre de tailles. "))
            : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("button", {
            type: "button",
            class: "px-4 py-2 text-sm border border-gray-300 rounded-md",
            onClick: _cache[3] || (_cache[3] = ($event: any) => (closeDeleteModal(false)))
          }, " Annuler "),
          _createElementVNode("button", {
            type: "button",
            disabled: isDeleting.value || (hasBoundProducts.value && !replacementPackageId.value),
            class: "px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed",
            onClick: confirmDeletePackage
          }, _toDisplayString(isDeleting.value ? 'Suppression...' : 'Supprimer'), 9, _hoisted_10)
        ])
      ]),
      _: 1
    }, 8, ["is-visible"])
  ], 64))
}
}

})